<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">玩家管理</h2>
      <div class="flex items-center mt-2">
        <router-link to="/admin/dashboard" class="text-gray-600 hover:text-gray-800">首页</router-link>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">玩家管理</span>
      </div>
    </div>
    
    <!-- 统计卡片 -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <i class="fas fa-users text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">总玩家数</p>
            <p class="text-2xl font-semibold text-gray-800">{{ formatNumber(stats.totalPlayers) }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">本月增长</p>
            <p class="text-sm text-green-600 font-semibold">+{{ stats.monthlyGrowth }}%</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <i class="fas fa-user-check text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">活跃玩家</p>
            <p class="text-2xl font-semibold text-gray-800">{{ formatNumber(stats.activePlayers) }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">本月累计</p>
            <p class="text-sm text-green-600 font-semibold">+{{ stats.activeGrowth }}%</p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-red-100 text-red-600">
            <i class="fas fa-user-slash text-xl"></i>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">禁用账户</p>
            <p class="text-2xl font-semibold text-gray-800">{{ formatNumber(stats.disabledPlayers) }}</p>
          </div>
        </div>
        <div class="mt-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">本月累计</p>
            <p class="text-sm text-red-600 font-semibold">+{{ stats.disabledGrowth }}%</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 筛选器 -->
    <SearchFilters
      :filter-config="filterConfig"
      :initial-filters="filters"
      @search="search"
      @reset="resetFilters"
    />
    
    <!-- 玩家列表表格 -->
    <div class="bg-white shadow rounded-md overflow-hidden">
      <div class="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center">
        <h3 class="text-lg leading-6 font-medium text-gray-900">玩家列表</h3>
        <button @click="exportData" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <i class="fas fa-download mr-2"></i> 导出数据
        </button>
      </div>
      
      <TableList
        :columns="columns"
        :items="players"
        :loading="loading"
        :total-items="totalItems"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        empty-text="暂无玩家数据"
        @page-change="handlePageChange"
      >
        <!-- 用户名 -->
        <template #cell-game_username="{ item }">
          <div class="flex items-center">
            <div class="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
              <span class="text-sm font-medium text-gray-700">{{ item.game_username.substring(0, 2) }}</span>
            </div>
            <div class="ml-4">
              <div class="text-sm font-medium text-gray-900">{{ item.game_username }}</div>
            </div>
          </div>
        </template>
        
        <!-- 用户货币 -->
        <template #cell-user_currency="{ item }">
          <span class="text-sm text-gray-900">{{ item.user_currency }}</span>
          <div v-if="item.currency !== item.user_currency" class="text-xs text-gray-500">汇率: {{ item.ugcr }}</div>
        </template>
        
        <!-- 状态 -->
        <template #cell-status="{ item }">
          <span v-if="item.status == 1" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            活跃
          </span>
          <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            禁用
          </span>
        </template>
        
        <!-- 操作 -->
        <template #cell-actions="{ item }">
          <button @click="viewPlayerWalletLogs(item.user_id)" class="text-blue-600 hover:text-blue-900 mr-3">
            查看余额记录
          </button>
        </template>
      </TableList>
    </div>
    
    <!-- 玩家余额变动记录模态框 -->
    <Modal
      :visible="walletLogsModalVisible"
      title="玩家余额变动记录"
      @close="closeWalletLogsModal"
    >
      <div v-if="loadingWalletLogs" class="flex justify-center items-center p-8">
        <div class="loader"></div>
        <span class="ml-3 text-lg">加载中...</span>
      </div>
      <div v-else-if="walletLogs.length === 0" class="p-8 text-center text-gray-600">
        <p>没有找到该玩家的余额变动记录</p>
      </div>
      <div v-else>
        <TableList
          :columns="walletLogsColumns"
          :items="walletLogs"
          :loading="loadingWalletLogs"
          :total-items="walletLogsTotalItems"
          :current-page="walletLogsPage"
          :items-per-page="walletLogsPerPage"
          empty-text="暂无余额变动记录"
          @page-change="(page) => loadPlayerWalletLogs(selectedPlayerId, page)"
        >
          <!-- 金额 -->
          <template #cell-amount="{ item }">
            <span :class="['text-sm font-medium', parseFloat(item.amount) >= 0 ? 'text-green-600' : 'text-red-600']">
              {{ parseFloat(item.amount) >= 0 ? '+' : '' }}{{ formatNumber(item.amount, 2) }}
            </span>
          </template>
          
          <!-- 交易前余额 -->
          <template #cell-before_balance="{ item }">
            <span class="text-sm text-gray-900">{{ formatNumber(item.before_balance, 2) }}</span>
          </template>
          
          <!-- 交易后余额 -->
          <template #cell-after_balance="{ item }">
            <span class="text-sm text-gray-900">{{ formatNumber(item.after_balance, 2) }}</span>
          </template>
          
          <!-- 游戏 -->
          <template #cell-game="{ item }">
            <span class="text-sm text-gray-900">{{ item.game || '-' }}</span>
          </template>
          
          <!-- 交易时间 -->
          <template #cell-created_at="{ item }">
            <span class="text-sm text-gray-500">{{ item.created_at }}</span>
          </template>
        </TableList>
      </div>
      
      <template #footer>
        <button @click="closeWalletLogsModal" class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm">
          关闭
        </button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import SearchFilters from '@/components/common/SearchFilters.vue';
import TableList from '@/components/common/TableList.vue';
import Modal from '@/components/common/Modal.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

// 状态变量
const loading = ref(false);
const players = ref([]);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(15);

// 统计数据
const stats = reactive({
  totalPlayers: 0,
  activePlayers: 0,
  disabledPlayers: 0,
  monthlyGrowth: 0,
  activeGrowth: 0,
  disabledGrowth: 0
});

// 筛选条件
const filters = reactive({
  game_username: '',
  user_id: '',
  status: '',
  register_at: '',
  currency: '',
  mer_id: '',
  site_id: ''
});

// 表格列配置
const columns = [
  { key: 'user_id', label: '用户ID' },
  { key: 'game_username', label: '游戏用户名' },
  { key: 'currency', label: '货币' },
  { key: 'user_currency', label: '用户货币' },
  { key: 'status', label: '状态' },
  { key: 'register_at', label: '注册时间' },
  { key: 'mer_id', label: '商户ID' },
  { key: 'site_id', label: '站点ID' },
  { key: 'actions', label: '操作' }
];

// 筛选器配置
const filterConfig = reactive([
  {
    id: 'game_username',
    type: 'input',
    label: '游戏用户名',
    placeholder: '输入游戏用户名',
    icon: 'fas fa-user'
  },
  {
    id: 'user_id',
    type: 'input',
    label: '用户ID',
    placeholder: '输入用户ID',
    icon: 'fas fa-id-card'
  },
  {
    id: 'status',
    type: 'select',
    label: '状态',
    placeholder: '选择状态',
    icon: 'fas fa-toggle-on',
    options: [
      { value: '', label: '全部状态' },
      { value: '1', label: '活跃' },
      { value: '0', label: '禁用' }
    ]
  },
  {
    id: 'register_at',
    type: 'date',
    label: '注册日期',
    placeholder: '选择日期',
    icon: 'fas fa-calendar'
  },
  {
    id: 'currency',
    type: 'select',
    label: '货币',
    placeholder: '选择货币',
    icon: 'fas fa-dollar-sign',
    options: [
      { value: '', label: '全部货币' },
      { value: 'HKD', label: 'HKD' },
      { value: 'USD', label: 'USD' },
      { value: 'CNY', label: 'CNY' }
    ]
  },
  {
    id: 'mer_id',
    type: 'input',
    label: '商户ID',
    placeholder: '输入商户ID',
    icon: 'fas fa-building'
  },
  {
    id: 'site_id',
    type: 'input',
    label: '站点ID',
    placeholder: '输入站点ID',
    icon: 'fas fa-sitemap'
  }
]);

// 余额记录模态框相关状态
const walletLogsModalVisible = ref(false);
const loadingWalletLogs = ref(false);
const walletLogs = ref([]);
const selectedPlayerId = ref(null);
const walletLogsPage = ref(1);
const walletLogsTotalPages = ref(1);
const walletLogsTotalItems = ref(0);
const walletLogsPerPage = ref(10);

// 表格列配置
const walletLogsColumns = [
  { key: 'id', label: '交易ID' },
  { key: 'amount', label: '金额' },
  { key: 'before_balance', label: '交易前余额' },
  { key: 'after_balance', label: '交易后余额' },
  { key: 'currency', label: '货币' },
  { key: 'game', label: '游戏' },
  { key: 'created_at', label: '交易时间' }
];

// 搜索方法
function search(newFilters) {
  Object.assign(filters, newFilters);
  currentPage.value = 1;
  loadPlayers();
}

// 重置筛选条件
function resetFilters() {
  // 清空所有筛选条件
  Object.keys(filters).forEach(key => {
    filters[key] = '';
  });
  
  // 清除localStorage中的缓存
  clearLocalStorageCache();
  
  // 重置页码并重新加载数据
  currentPage.value = 1;
  loadPlayers();
}

// 清除localStorage缓存
function clearLocalStorageCache() {
  // 清除玩家筛选相关的所有缓存
  localStorage.removeItem('filter_player_username');
  localStorage.removeItem('filter_player_id');
  
  // 可以根据需要清除其他相关缓存
  console.log('已清除玩家筛选缓存');
}

// 加载玩家列表
function loadPlayers(page = 1) {
  loading.value = true;
  currentPage.value = page;

  apiClient.getPlayers(filters, page, itemsPerPage.value)
    .then(response => {
      const data = response.data || {};
      players.value = data.players || [];
      totalItems.value = data.total || 0;
      loading.value = false;
    })
    .catch(error => {
      console.error('加载玩家列表失败:', error);
      loading.value = false;
    });
}

// 加载统计数据
function loadPlayerStats() {
  apiClient.getPlayerStats()
    .then(response => {
      const data = response.data?.stats || response.stats || {};
      stats.totalPlayers = data.total_players || 0;
      stats.activePlayers = data.active_players || 0;
      stats.disabledPlayers = 0
      
      // 计算增长率
      stats.monthlyGrowth = data.monthly_growth || 0;
      stats.activeGrowth = data.active_growth || 0;
      stats.disabledGrowth = data.disabled_growth || 0;
    })
    .catch(error => {
      console.error('加载统计数据失败:', error);
    });
}

// 查看玩家余额变动记录
function viewPlayerWalletLogs(userId) {
  selectedPlayerId.value = userId;
  walletLogsModalVisible.value = true;
  walletLogsPage.value = 1; // 重置页码到第一页
  walletLogs.value = []; // 清空之前的数据
  walletLogsTotalItems.value = 0; // 重置总数量
  loadPlayerWalletLogs(userId, 1);
}

// 加载玩家余额变动记录
function loadPlayerWalletLogs(userId, page = 1) {
  loadingWalletLogs.value = true;
  walletLogsPage.value = page;
  console.log(`加载玩家ID ${userId} 的余额记录，第 ${page} 页`);

  apiClient.getWalletLogs({ user_id: userId }, page, walletLogsPerPage.value)
    .then(response => {
      const data = response.data || {};
      walletLogs.value = data.logs || [];
      walletLogsTotalItems.value = data.total || 0;
      walletLogsTotalPages.value = Math.ceil(walletLogsTotalItems.value / walletLogsPerPage.value);
      loadingWalletLogs.value = false;
      
      console.log(`加载成功: 共 ${walletLogsTotalItems.value} 条记录, ${walletLogsTotalPages.value} 页`);
    })
    .catch(error => {
      console.error('加载玩家余额变动记录失败:', error);
      walletLogs.value = [];
      loadingWalletLogs.value = false;
    });
}

// 关闭余额变动记录模态框
function closeWalletLogsModal() {
  walletLogsModalVisible.value = false;
}

// 导出数据
function exportData() {
  // 构建导出URL
  const params = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      params.append(key, filters[key]);
    }
  });
  
  const exportUrl = `/admin/api/players/export?${params.toString()}`;
  window.open(exportUrl, '_blank');
}

// 处理页码变化
function handlePageChange(page) {
  loadPlayers(page);
}

// 格式化数字
function formatNumber(number, fractionDigits = 0) {
  return Number(number).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

// 生命周期钩子
onMounted(() => {
  // 加载基本数据
  loadPlayerStats();
  
  // 检查URL参数和localStorage以应用用户筛选条件
  checkAndApplyUserFilter();
  
  // 加载玩家列表
  loadPlayers();
});

// 检查并应用用户筛选条件
function checkAndApplyUserFilter() {
  // 1. 检查URL参数
  const urlParams = new URLSearchParams(window.location.search);
  const usernameFromUrl = urlParams.get('username');
  const userIdFromUrl = urlParams.get('user_id');
  
  // 2. 检查localStorage
  const usernameFromStorage = localStorage.getItem('filter_player_username');
  const userIdFromStorage = localStorage.getItem('filter_player_id');
  
  // 3. 优先使用URL参数，其次使用localStorage
  if (usernameFromUrl) {
    console.log('从URL应用用户筛选: ', usernameFromUrl);
    filters.game_username = usernameFromUrl;
  } else if (usernameFromStorage) {
    console.log('从Storage应用用户筛选: ', usernameFromStorage);
    filters.game_username = usernameFromStorage;
  }
  
  if (userIdFromUrl) {
    console.log('从URL应用ID筛选: ', userIdFromUrl);
    filters.user_id = userIdFromUrl;
  } else if (userIdFromStorage) {
    console.log('从Storage应用ID筛选: ', userIdFromStorage);
    filters.user_id = userIdFromStorage;
  }
  
  // 4. 清除localStorage中的筛选条件，防止影响后续操作
  localStorage.removeItem('filter_player_username');
  localStorage.removeItem('filter_player_id');
}
</script>

<style scoped>
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 